<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { isSuccess } from '@/@vxapp/@Utils/http'
import { useProductCategoryStore } from '@/@vxapp/ProductCategories/useProductCategoryStore'
import type { ProductCategoryData } from '@/@vxapp/ProductCategories/useProductCategoryStore'
import { useNotificationsStore } from '@/@vxapp/useNotificationsStore'
import ProductCategoryDialogSimple from '@/components/Dialogs/ProductCategories/ProductCategoryDialogSimple.vue'
import ProductCategoryListToolbar from '@/components/ServiceLists/ProductCategories/ProductCategoryListToolbar.vue'
import type {
  ProductCategoryListQueryParams,
  ProductCategoryPaginationMeta,
} from '@/components/ServiceLists/ProductCategories/productCategoryListTypes'
import ProductCategoriesDatatable from '@/components/Tables/ProductCategories/ProductCategoriesDatatable.vue'
import {
  areProductCategoryListQueriesEqual,
  buildProductCategoryListUrlQuery,
  hasProductCategoryListUrlStateChanged,
  parseProductCategoryListUrlState,
} from '@/pages/apps/product-categories/list/urlState'

definePage({
  meta: { ...PageMeta.Categories.List.Meta, pageTitle: PageMeta.Categories.List.Title },
})

const productCategoryStore = useProductCategoryStore()
const notify = useNotificationsStore()
const route = useRoute()
const router = useRouter()

const categoriesDataTable =
  useTemplateRef<InstanceType<typeof ProductCategoriesDatatable>>('categoriesDataTable')

const isDialogVisible = shallowRef(false)
const isCreateMode = shallowRef(true)
const productCategoryData = ref<ProductCategoryData>({} as ProductCategoryData)
const isRouteQueryReady = shallowRef(false)
const resultCount = shallowRef<number | null>(null)

const defaultQueryParams: ProductCategoryListQueryParams = {
  page: PaginationField.QueryParams.page,
  limit: PaginationField.QueryParams.limit,
  sort: PaginationField.QueryParams.sort,
  search: undefined,
}

const queryParams = computed<ProductCategoryListQueryParams>(() => ({
  page: Number(productCategoryStore.queryParams.page),
  limit: Number(productCategoryStore.queryParams.limit),
  sort: String(productCategoryStore.queryParams.sort),
  search:
    typeof productCategoryStore.queryParams.search === 'string'
      ? productCategoryStore.queryParams.search
      : undefined,
}))

function applyRouteQueryParams() {
  const routeQueryParams = parseProductCategoryListUrlState(
    route.query,
    defaultQueryParams
  ) as ProductCategoryListQueryParams

  if (!hasProductCategoryListUrlStateChanged(queryParams.value, routeQueryParams)) return false

  productCategoryStore.queryParams = {
    ...productCategoryStore.queryParams,
    ...routeQueryParams,
  }

  return true
}

async function syncRouteQuery() {
  const nextQuery = buildProductCategoryListUrlQuery(
    route.query,
    queryParams.value,
    defaultQueryParams
  )

  if (areProductCategoryListQueriesEqual(route.query, nextQuery)) return

  await router.replace({ query: nextQuery })
}

async function refreshCategories() {
  await categoriesDataTable.value?.fetchProductCategories()
}

async function updateQueryParams(nextQueryParams: Partial<ProductCategoryListQueryParams>) {
  productCategoryStore.queryParams = {
    ...productCategoryStore.queryParams,
    ...nextQueryParams,
  }

  if (isRouteQueryReady.value) await syncRouteQuery()

  await nextTick()
  await refreshCategories()
}

function handleLoaded(pagination: ProductCategoryPaginationMeta) {
  resultCount.value = pagination.entriesFound
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({ limit, page: 1 })
}

async function handleClear() {
  productCategoryStore.queryParams = {
    ...productCategoryStore.queryParams,
    ...defaultQueryParams,
  }

  if (isRouteQueryReady.value) await syncRouteQuery()

  await nextTick()
  await refreshCategories()
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

async function handleCreateCategory() {
  try {
    await productCategoryStore.fetchTemplate()
    productCategoryData.value = productCategoryStore.template as unknown as ProductCategoryData
    isCreateMode.value = true
    isDialogVisible.value = true
  } catch {
    notify.error('Failed to load category template.')
  }
}

async function showEditModal(categoryGuid: string) {
  try {
    await productCategoryStore.fetch(categoryGuid)
    productCategoryData.value =
      productCategoryStore.productCategory as unknown as ProductCategoryData
    isCreateMode.value = false
    isDialogVisible.value = true
  } catch {
    notify.error('Failed to load product category.')
  }
}

async function handleCreate(data: ProductCategoryData) {
  try {
    const response = await productCategoryStore.create(data)

    if (isSuccess(response.responseCode)) {
      notify.success('Category created successfully')
      await refreshCategories()
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error'

    notify.error(`Failed to create category: ${message}`)
  }
}

async function handleUpdate(data: ProductCategoryData) {
  try {
    const response = await productCategoryStore.update(data)

    if (isSuccess(response.responseCode)) {
      notify.success('Category updated successfully')
      await refreshCategories()
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error'

    notify.error(`Failed to update category: ${message}`)
  }
}

onMounted(async () => {
  await productCategoryStore.init()
  applyRouteQueryParams()
  isRouteQueryReady.value = true
  await nextTick()
  await refreshCategories()
})

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQueryParams()) return

    await nextTick()
    await refreshCategories()
  },
  { deep: true }
)
</script>

<template>
  <main class="product-categories-workspace">
    <VCard class="product-categories-workspace__hero">
      <VCardItem>
        <div class="product-categories-workspace__hero-content">
          <div>
            <span class="product-categories-workspace__eyebrow">Catalog structure</span>
            <VCardTitle class="product-categories-workspace__title">
              Product categories
            </VCardTitle>
            <VCardSubtitle class="product-categories-workspace__subtitle">
              Shape a searchable product hierarchy with visibility, ownership, and display order.
            </VCardSubtitle>
          </div>
          <div class="product-categories-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount === null ? '—' : resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>

      <VDivider />

      <VCardActions class="product-categories-workspace__actions">
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreateCategory"
        >
          New category
        </VBtn>
        <VBtn
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="refreshCategories"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>

    <ProductCategoryListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-refreshing="productCategoryStore.isLoading"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClear"
      @refresh="refreshCategories"
    />

    <ProductCategoriesDatatable
      ref="categoriesDataTable"
      :query-params="queryParams"
      @show-edit-modal="showEditModal"
      @page-change="handlePageChange"
      @loaded="handleLoaded"
    />
  </main>

  <ProductCategoryDialogSimple
    v-if="isDialogVisible"
    v-model:is-dialog-visible="isDialogVisible"
    :product-category-data="productCategoryData"
    :is-create-mode="isCreateMode"
    @create="handleCreate"
    @update="handleUpdate"
  />
</template>

<style scoped lang="scss">
.product-categories-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.product-categories-workspace__hero {
  overflow: hidden;
  min-inline-size: 0;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.product-categories-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.product-categories-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.product-categories-workspace__title {
  overflow: visible;
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
  text-overflow: clip;
  white-space: normal;
}

.product-categories-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.product-categories-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.product-categories-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.product-categories-workspace__metric strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1.5rem;
  font-weight: 590;
  letter-spacing: -0.04em;
}

.product-categories-workspace__actions {
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.product-categories-workspace__actions :deep(.v-btn) {
  min-block-size: 2.5rem;
  border-radius: 6px;
}

@media (max-width: 599px) {
  .product-categories-workspace {
    gap: 1rem;
  }

  .product-categories-workspace__hero-content {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
  }

  .product-categories-workspace__metric {
    inline-size: 100%;
  }

  .product-categories-workspace__actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    padding: 0.75rem;
  }

  .product-categories-workspace__actions :deep(.v-btn) {
    inline-size: 100%;
  }
}
</style>
